import { coreTypes } from "@core/core-types.di";
import { HttpFailedRequestError } from "@core/data/infrastructures/http/errors/http-failed-request.error";
import { type Http } from "@core/data/infrastructures/http/http";
import { HttpErrorCodeEnum } from "@core/data/infrastructures/http/http-error-response";
import { OrderMapper } from "@core/data/mappers/order.mapper";
import { ValidationError } from "@core/domain/errors/validation.error";
import { Order } from "@core/domain/models/order.model";
import { Pagination } from "@core/domain/models/pagination";
import { Either } from "@core/domain/types/either";
import { plainToClass } from "class-transformer";
import { inject, injectable } from "inversify";
import { DepositSearchFilters } from "../../domain/models/deposit-search-filters";
import {
    CreateDeposit,
    CreateSplitDeposit,
    Deposit,
    SplitDeposit,
} from "../../domain/models/deposit.model";
import { IdentificationType } from "../../domain/models/identification-type.model";
import { DepositGeneralTypesDto } from "../dto/deposit-types.dto";
import {
    CreateSplitDepositDto,
    DepositDto,
    DepositResultDto,
    SplitDepositDto,
} from "../dto/deposit.dto";
import { IdentificationTypeDto } from "../dto/identification-type.dto";
import { DepositMapper } from "../mappers/deposit/deposit.mapper";
import { IdentificationTypeMapper } from "../mappers/deposit/identification-type.mapper";
import { EntityDepositError } from "@entity/domain/errors/entity.error";
import { DepositMapperError } from "@entity/domain/errors/deposit/deposit-mapper.error";

const DEPOSIT_PATH = "/deposits/";
const SPLIT_DEPOSIT_PATH = "/projects/split_deposits/";

@injectable()
export class DepositDatasource {
    constructor(
        @inject(coreTypes.infrastructure.Http)
        private readonly http: Http,
        @inject(DepositMapper)
        private readonly depositMapper: DepositMapper,
        @inject(OrderMapper)
        private readonly orderMapper: OrderMapper,
        @inject(IdentificationTypeMapper)
        private readonly identificationTypeMapper: IdentificationTypeMapper,
    ) { }

    async fetchAllDepositTypes(): Promise<
        Either<EntityDepositError, DepositGeneralTypesDto>
    > {
        const responseResult = await this.http.get<DepositGeneralTypesDto>(
            `${DEPOSIT_PATH}types/`,
        );

        return responseResult
            .mapLeft(() => new EntityDepositError("fetchDepositTypesError"))
            .map((response) => {
                const allDepositTypes = plainToClass(DepositGeneralTypesDto, response.data);
                if (!allDepositTypes)
                    return {
                        types: [],
                        own_financing_types: [],
                        subvention_types: [],
                        others_source_financing_type: [],
                    };
                return allDepositTypes;
            });
    }

    async getAllIdentificationDocumentTypes(): Promise<
        Either<EntityDepositError, IdentificationType[]>
    > {
        const identificationTypesResult = await this.http.get<
            IdentificationTypeDto[]
        >(`${DEPOSIT_PATH}identification_types/`);

        return identificationTypesResult
            .mapLeft(() => new EntityDepositError("fetchIdentificationTypesError"))
            .map((response) =>
                response.data.mapNotNull((identificationType) =>
                    this.identificationTypeMapper.map(
                        plainToClass(IdentificationTypeDto, identificationType),
                    ),
                ),
            );
    }

    async fetchAll(): Promise<Either<EntityDepositError, Deposit[]>> {
        const depositResult =
            await this.http.get<DepositResultDto>(DEPOSIT_PATH);

        return depositResult
            .mapLeft(() => new EntityDepositError("fetchDepositsError"))
            .map((response) =>
                response.data.results.mapNotNull((deposit) =>
                    this.depositMapper.map(plainToClass(DepositDto, deposit)),
                ),
            );
    }

    async fetchBy(
        pagination: Pagination,
        filters?: DepositSearchFilters,
        order?: Order<Deposit>,
    ): Promise<Either<EntityDepositError, Deposit[]>> {
        // Use a plain object for query
        const query: Record<string, any> = {
            limit: pagination.pageSize,
            offset: pagination.offset,
        };

        if (filters?.name) query["search"] = filters.name;
        if (filters?.type) query["type"] = filters.type;
        if (filters?.depositDateFrom)
            query["deposit_date__gte"] = filters.depositDateFrom.toISODate();
        if (filters?.depositDateTo)
            query["deposit_date__lte"] = filters.depositDateTo.toISODate();

        if (order) {
            // Define a minimal order field mapping for DepositDto fields
            const orderFieldMap = {
                id: "id",
                concept: "concept",
                amount: "amount",
                depositDate: "deposit_date",
                name: "name",
                depositTypeId: "deposit_type_id",
                depositSubtypeId: "deposit_subtype_id",
                depositCategoryId: "deposit_category_id",
                depositScopeId: "deposit_scope_id",
                scopeDetail: "scope_detail",
                depositDateFrom: "deposit_date__gte",
                depositDateTo: "deposit_date__lte",
                // Add more mappings as needed for any sortable Deposit fields
            };
            const orderQuery = this.orderMapper.mapToDto<Deposit, DepositDto>(
                order.field,
                order.direction,
                orderFieldMap as any
            );
            if (orderQuery) query["ordering"] = orderQuery;
        }

        const depositsResult = await this.http.get<DepositResultDto>(
            DEPOSIT_PATH,
            {
                query,
            },
        );

        return depositsResult
            .mapLeft(() => new EntityDepositError("fetchFilteredDepositsError"))
            .map((response) =>
                response.data.results.mapNotNull((depositDto) =>
                    this.depositMapper.map(
                        plainToClass(DepositDto, depositDto),
                    ),
                ),
            );
    }

    async create(
        deposit: CreateDeposit,
    ): Promise<Either<ValidationError | EntityDepositError | DepositMapperError, Deposit>> {
        const depositDto = this.depositMapper.mapToCreateDepositDto(deposit);
        const createDepositResult = await this.http.post<DepositDto>(
            DEPOSIT_PATH,
            depositDto,
        );

        return createDepositResult
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                }

                return new EntityDepositError("createDepositError");
            })
            .flatMap((response) => {
                const depositCreated = this.depositMapper.map(
                    plainToClass(DepositDto, response.data),
                );

                if (!depositCreated) return Either.Left(new DepositMapperError());

                return Either.Right(depositCreated);
            });
    }

    async edit(
        deposit: Deposit,
    ): Promise<Either<ValidationError | EntityDepositError | DepositMapperError, Deposit>> {
        const depositDto = this.depositMapper.mapToDepositDto(deposit);

        const editDepositResult = await this.http.patch<DepositDto>(
            `${DEPOSIT_PATH}${deposit.id}/`,
            depositDto,
        );

        return editDepositResult
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                }

                return new EntityDepositError("editDepositError");
            })
            .flatMap((response) => {
                const depositEdited = this.depositMapper.map(
                    plainToClass(DepositDto, response.data),
                );

                if (!depositEdited) return Either.Left(new DepositMapperError());

                return Either.Right(depositEdited);
            });
    }

    async createSplitDeposit(
        createSplitDeposit: CreateSplitDeposit,
    ): Promise<Either<ValidationError | EntityDepositError | DepositMapperError, SplitDeposit>> {
        const createSplitDepositDto: CreateSplitDepositDto = {
            deposit: createSplitDeposit.depositId,
            project: createSplitDeposit.projectId,
            assigned_percentage: createSplitDeposit.assignedPercentage,
        };

        const splitDepositResult = await this.http.post<SplitDepositDto>(
            SPLIT_DEPOSIT_PATH,
            createSplitDepositDto,
        );

        return splitDepositResult
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                }

                return new EntityDepositError("createSplitDepositError");
            })
            .flatMap((response) => {
                const splitDepositCreated = this.depositMapper.splitDepositMap(
                    plainToClass(SplitDepositDto, response.data),
                );

                if (!splitDepositCreated)
                    return Either.Left(new DepositMapperError());

                return Either.Right(splitDepositCreated);
            });
    }

    async editSplitDeposit(
        splitDeposit: SplitDeposit,
    ): Promise<Either<ValidationError | EntityDepositError | DepositMapperError, SplitDeposit>> {
        const splitDepositDto: SplitDepositDto = {
            id: splitDeposit.id,
            deposit: splitDeposit.depositId,
            project: splitDeposit.projectId,
            assigned_percentage: splitDeposit.assignedPercentage,
        };

        const editSplitDepositResult = await this.http.patch<SplitDepositDto>(
            `${SPLIT_DEPOSIT_PATH}${splitDeposit.id}/`,
            splitDepositDto,
        );

        return editSplitDepositResult
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                }

                return new EntityDepositError("editSplitDepositError");
            })
            .flatMap((response) => {
                const splitDepositEdited = this.depositMapper.splitDepositMap(
                    plainToClass(SplitDepositDto, response.data),
                );

                if (!splitDepositEdited)
                    return Either.Left(new DepositMapperError());

                return Either.Right(splitDepositEdited);
            });
    }

    async deleteSplitDeposit(
        splitDepositId: number,
    ): Promise<Either<EntityDepositError, boolean>> {
        const deleteSplitDepositResult = await this.http.delete(
            `${SPLIT_DEPOSIT_PATH}${splitDepositId}/`,
        );

        return deleteSplitDepositResult
            .mapLeft(() => new EntityDepositError("deleteSplitDepositError"))
            .map(() => true);
    }
    async delete(id: number): Promise<Either<EntityDepositError, boolean>> {
        const deleteResult = await this.http.delete(`${DEPOSIT_PATH}${id}/`);

        return deleteResult.mapLeft(() => new EntityDepositError("deleteDepositError")).map(() => true);
    }
}
